import { useContext, useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import styles from "../../content/pages/homepage.module.css";
import { WidgetContext } from "src/contexts/WidgetContext";
import { getCommonLeagueData } from "src/api/getCommonLeagueData";
import { validateTeamData } from "src/utils/teamValidation";
import { postUserPreferences } from "src/api/postUserPreferences";
import { App } from "@capacitor/app";
import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import { BackgroundTask } from "@capawesome/capacitor-background-task";
import { generateFunnyAudio } from "src/api/generateFunnyAudio";
import { applyLegacyLipSyncOrSkip, isCapacitorNative, isVegaLipSyncEnabled, prefetchVegaFromAudioUrl, prepareVegaFromAudioUrl, tryApplyVegaFromAudioUrlSync } from "src/lipsync/vegaLipSync";
import { audioService, AudioPriority } from "src/services/audioService";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { redirectToAuth0NoCredits } from "src/utils/auth0NoCredits";
import { trackLeagueSelection, trackManagedTeamsSummary, trackEndpointResponseTime } from "src/utils/analyticsCapFirebase";
import { getCommonLeagueDataStream } from "src/api/getCommonLeagueDataStream";
import { getSuperAnalysisStream } from "src/api/getSuperAnalysisStream";
import { startPollingForCompletion } from "src/utils/streamPolling";
import { isEspnTokenExpiring } from "src/utils/espnTokenValidation";
import { shouldOpenEspnReauth } from "src/utils/espnSession";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { getEstimatedTimes } from "src/api/getEstimatedTimes";
import { CharacterEmbedRef } from "src/components/CharacterViewer/CharacterEmbed";
import { SectionData } from "src/mockup/UserPreferencesData";
import { frontEnvNumber } from "src/services/runtimeFrontConfig";
import { isDraftAdviceDisabledByLeagueStatus, isDraftAdviceDisabledForPlatform } from "src/utils/draftAdvicePlatformGate";
import { isIdpFromCommonLeagueData } from "src/utils/isIdpLeague";
import { IdpDialog } from "src/components/IdpDialog/IdpDialog";
import { isUnsyncedBlockedAgent, isUnsyncedPlatform } from "src/constants/unsyncedLeague";

const noDraftErrorMessage = "Rosters are empty as this league has not had it's draft yet. Try a different league.";

interface Props {
  handlePartnerSelected: (partner: string) => void;
  teamsData: any;
  handleLeagueId: (leagueId: string) => void;
  setLoadingNews: (loading: boolean) => void;
  setLoadingWaiver: (loading: boolean) => void;
  setLoadingStart: (loading: boolean) => void;
  setLoadingRoster: (loading: boolean) => void;
  setLoadingRankings: (loading: boolean) => void;
  setLoadingAllRankings: (loading: boolean) => void;
  setLoadingRundown: (loading: boolean) => void;
  setLoadingDraftAdvice: (loading: boolean) => void;
  fetchNewsData: (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal?: AbortSignal,
    yahooToken?: string,
    forceRefresh?: boolean,
    calculatorSettings?: any
  ) => Promise<void>;
  fetchWaiverData: (leagueId: string, signal?: AbortSignal, yahooToken?: string) => Promise<void>;
  fetchWaiverDataStream: (leagueId: string, platformId: string, teamName: string, signal?: AbortSignal, yahooToken?: string, forceRefresh?: boolean, calculatorSettings?: any, modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE", checkCacheOnly?: boolean) => Promise<any>;
  fetchStartDataStream: (leagueId: string, platformId: string, teamName: string, signal?: AbortSignal, yahooToken?: string, forceRefresh?: boolean, calculatorSettings?: any, modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE", checkCacheOnly?: boolean) => Promise<any>;
  fetchRostersData: (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal?: AbortSignal,
    yahooToken?: string,
    forceRefresh?: boolean,
    calculatorSettings?: any
  ) => Promise<void>;
  fetchRankingsData: (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal?: AbortSignal,
    yahooToken?: string,
    forceRefresh?: boolean,
    calculatorSettings?: any
  ) => Promise<void>;
  fetchAllRankingsData: (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal?: AbortSignal,
    yahooToken?: string,
    forceRefresh?: boolean,
    calculatorSettings?: any
  ) => Promise<void>;
  fetchRundownDataStream: (leagueId: string, platformId: string, teamName: string, signal?: AbortSignal, yahooToken?: string, forceRefresh?: boolean, calculatorSettings?: any, modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE", checkCacheOnly?: boolean) => Promise<any>;
  fetchDraftAdviceDataStream: (leagueId: string, platformId: string, teamName: string, signal?: AbortSignal, yahooToken?: string, forceRefresh?: boolean, calculatorSettings?: any, modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE", checkCacheOnly?: boolean) => Promise<any>;
  userDataPlatform: any;
  setHasLoadingStarted: (started: boolean) => void;
  yahooToken: string | null;
  setSectionData: (data: SectionData) => void;
  setMessage: (message: any) => void;
  selectedTeams: any;
  expanded: string;
  setExpanded: (value: string) => void;
  setEndpointErrors: (errors: any) => void;
  isYahooSelected: boolean;
  loggingInTo: string;
  setLoggingInTo: (platform: string) => void;
  setErrorMessages: (errors: any) => void;
  fetchSuperFunctionData: (leagueId: string, signal?: AbortSignal, yahooToken?: string) => Promise<any>;
  setLoadingSuperFunction: (loading: boolean) => void;
  commonLeagueDataFailed: boolean;
  setCommonLeagueDataFailed: (failed: boolean) => void;
  returnedFromBackground: boolean;
  setReturnedFromBackground: (returned: boolean) => void;
  setTeamsdata: (teamsdata: any) => void;
  setWaiverData: (waiverData: any) => void;
  setNewsData: (newsData: any) => void;
  setStartData: (startData: any) => void;
  setRostersData: (rostersData: any) => void;
  setRankingsData: (rankingsData: any) => void;
  setAllRankingsData: (allRankingsData: any) => void;
  setRundownData: (rundownData: any) => void;
  setDraftAdviceData: (DraftAdviceData: any) => void;
  isHandlingTeamSelection: React.MutableRefObject<boolean>;
  newClickOnPlatofrm: boolean;
  setNewClickOnPlatofrm: (newClickOnPlatofrm: boolean) => void;
  setIsChangingPlatform: (isChangingPlatform: boolean) => void;
  setErrorMessageNoDraft: React.Dispatch<React.SetStateAction<any>>;
  setWaiverStreamState: React.Dispatch<React.SetStateAction<any>>;
  setStartStreamState: React.Dispatch<React.SetStateAction<any>>;
  setRundownStreamState: React.Dispatch<React.SetStateAction<any>>;
  setDraftAdviceStreamState: React.Dispatch<React.SetStateAction<any>>;
  setLoginError: React.Dispatch<React.SetStateAction<any>>;
  setThoughtsCommon: React.Dispatch<React.SetStateAction<string>>;
  isSuperAnalysisComplete: boolean;
  setIsSuperAnalysisComplete: React.Dispatch<React.SetStateAction<boolean>>;
  activeRequests: React.MutableRefObject<any>;
  setRefreshingLeagueData: React.Dispatch<React.SetStateAction<boolean>>;
  setLastUpdatedTimestamps: React.Dispatch<React.SetStateAction<any>>;
  setEstimatedTimes: React.Dispatch<React.SetStateAction<any>>;
  // Calculator settings from LeagueSettings component
  calculatorSettings?: any;
  setLeagueSettingsChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedModel: "RotoGPT 5.0" | "RotoGPT FREE";
  userSubscription: string; // User subscription type for Super Analysis model selection
  // Character ref for lip sync with funny audio
  characterRef?: React.RefObject<CharacterEmbedRef>;
  // When true, after Super Analysis completes trigger waiver/start/rundown/draft streams in parallel
  aiSettings?: Record<string, boolean>;
  // Credit validation for auto-process (same as SectionHomePage): open paywall or bench drawer when no credits
  enableCreditValidation?: boolean;
  setIsBenchDrawerOpen?: (open: boolean) => void;
  setIsPaymentOpen?: (open: boolean) => void;
  setInitialPaymentView?: (view: string) => void;
  // Optional rate limit drawer opener for handling 429 in super analysis
  openRateLimitDrawer?: (retryAfterSeconds?: number) => void;
}

// Interface to handle the handlePartnerClick function
export interface TeamsHomePageRef {
  handlePartnerClick: (partner: string, leagueId: string, platformId?: string, signal?: AbortSignal, forceRefresh?: boolean, calculatorSettings?: any) => Promise<void>;
  playFunnyTeamAudio: (teamName: string, leagueId: string, platformId: string) => void;
}

// Function to check if the cache is valid
const isCacheValid = (data, currentSelectedPlatformIdRef, partner, leagueId) => {
  // Check if there is data and if it has a timestamp
  if (!data || !data.commonLeagueData?.timestamp) return false;

  // Check if platform matches
  if (data.commonLeagueData.platform !== currentSelectedPlatformIdRef.current) return false;

  // Check if team and league match
  if (data.commonLeagueData.teamName !== partner || data.commonLeagueData.leagueId !== leagueId) return false;

  // Get the cache time and the current time
  const cacheTime = new Date(data.commonLeagueData.timestamp).getTime();
  const currentTime = new Date().getTime();

  // Check if the cache is less than one hour old
  const oneHour = 60 * 60 * 1000;
  const fiveMinutes = 5 * 60 * 1000;
  return currentTime - cacheTime < oneHour;
};

const TeamsDropdown = forwardRef<TeamsHomePageRef, Props>(
  (
    {
      handlePartnerSelected,
      teamsData,
      handleLeagueId,
      setLoadingNews,
      setLoadingWaiver,
      setLoadingStart,
      setLoadingRoster,
      setLoadingRankings,
      setLoadingAllRankings,
      setLoadingRundown,
      setLoadingDraftAdvice,
      fetchNewsData,
      fetchWaiverData,
      fetchWaiverDataStream,
      fetchStartDataStream,
      fetchRostersData,
      fetchRankingsData,
      fetchAllRankingsData,
      fetchRundownDataStream,
      fetchDraftAdviceDataStream,
      userDataPlatform,
      setHasLoadingStarted,
      yahooToken,
      setMessage,
      selectedTeams,
      expanded,
      setExpanded,
      setEndpointErrors,
      loggingInTo,
      setLoggingInTo,
      setErrorMessages,
      setCommonLeagueDataFailed,
      returnedFromBackground,
      setReturnedFromBackground,
      newClickOnPlatofrm,
      setNewClickOnPlatofrm,
      setIsChangingPlatform,
      setErrorMessageNoDraft,
      setTeamsdata,
      setWaiverData,
      setNewsData,
      setStartData,
      setRostersData,
      setRankingsData,
      setAllRankingsData,
      setRundownData,
      setDraftAdviceData,
      setWaiverStreamState,
      setStartStreamState,
      setRundownStreamState,
      setDraftAdviceStreamState,
      setLoginError,
      setThoughtsCommon,
      isSuperAnalysisComplete,
      setIsSuperAnalysisComplete,
      activeRequests,
      setRefreshingLeagueData,
      setLastUpdatedTimestamps,
      setEstimatedTimes,
      calculatorSettings,
      setLeagueSettingsChanged,
      selectedModel,
      userSubscription,
      characterRef,
      aiSettings,
      enableCreditValidation,
      setIsBenchDrawerOpen,
      setIsPaymentOpen,
      setInitialPaymentView,
      openRateLimitDrawer,
    },
    ref
  ) => {
    // import of context function to make the team name dynamic
    const {
      pocToken,
      setMessage: widgetSetMessage,
      handleTeamSelection,
      sectionData,
      updateSectionData,
      setDataLoadedFromTeamsPage,
      selectedTeamInfo,
      cancelPendingRequests,
      setCurrentAbortController,
      leaguePlatforms,
      getValidYahooToken,
      setSelectedTeams,
      setCommonLeagueLeagueType,
      isTransitioning,
      setIsTransitioning,
      userDataCache,
      teamsDataCache,
      lastUpdatedTimestamps,
      calculatorSettingsMap,
      keeperDrawerSeenMap,
      freeCredits,
      credits,
      allowedButtonsSections,
      isLoggedInAuth0ExperienceGlobal,
      setIsIdpLeagueBlocked,
    } = useContext(WidgetContext);

    const isMobile = mobile || isMobileWebBrowser();

    // Kept for reference / LeagueSelector parity — CSS ellipsis below handles single-line on mobile
    // const truncateLeagueName = (name: string): string => {
    //   return name.length > 30 ? `${name.substring(0, 30)}...` : name;
    // };

    // Feature flags (backend-controlled) to prevent unwanted endpoint executions.
    // We default to `true` to avoid breaking flows if the config hasn't loaded yet.
    const showNews = allowedButtonsSections?.showNews ?? true;
    const showRosters = allowedButtonsSections?.showRosters ?? true;
    const showAllRankings = allowedButtonsSections?.showAllRankings ?? true;
    const showWaiver = allowedButtonsSections?.showWaiver ?? true;
    const showStart = allowedButtonsSections?.showStart ?? true;
    const showTeamAnalysis = allowedButtonsSections?.showTeamAnalysis ?? true; // Maps to "rundown" stream/section
    const showDraftAdvice = allowedButtonsSections?.showDraftAdvice ?? true;
    const disabledPlatformDraftAdvice = allowedButtonsSections?.disabledPlatformDraftAdvice ?? [];


    // Determine model for Super Analysis based on user subscription
    // If subscription is "FREE", use "RotoGPT FREE", otherwise use "RotoGPT 5.0"
    const superAnalysisModel: "RotoGPT 5.0" | "RotoGPT FREE" = userSubscription?.toLowerCase() === "free" ? "RotoGPT FREE" : "RotoGPT 5.0";
    const [partnerClicked, setPartnerClicked] = useState(false);
    const [showIdpDialog, setShowIdpDialog] = useState(false);
    const isSuperAnalysisCompleteRef = useRef(isSuperAnalysisComplete);

    // Ref to manage the abort controller for audio requests across clicks
    const audioAbortControllerRef = useRef<AbortController | null>(null);

    // Load estimated times from backend when component mounts
    const loadEstimatedTimes = async () => {
      try {
        const accessToken = await getSafeAccessAuth0Token();
        if (accessToken) {
          const response = await getEstimatedTimes(accessToken);
          setEstimatedTimes(response.estimatedTime);
        }
      } catch (error) {
        console.error("Error loading estimated times:", error);
        // Use default values as fallback
        // setEstimatedTimes({
        //   waiver: 90000,
        //   start: 90000,
        //   rundown: 90000,
        //   news: 13200,
        //   rankings: 13200,
        //   rosters: 13200,
        // });
      }
    };

    // Ref to track managed teams for the current context
    const managedTeamsInfoRef = useRef({
      authId: null as string | null,
      platformId: null as string | null,
      userId: null as string | null, //atraper, jplaf id for example
      totalTeams: 0,
      managedSet: new Set<string>(), // league_id of the teams selected
    });

    // Helper function to send the summary event
    const sendManagedTeamsSummaryIfNeeded = () => {
      const info = managedTeamsInfoRef.current;
      // Only send if we've counted at least one team
      if (info.managedSet.size > 0 && info.platformId && info.userId) {
        trackManagedTeamsSummary(info.authId, info.platformId, info.userId, info.managedSet.size, info.totalTeams);
      }
    };

    // This useEffect handles the final case: if the user logs out, etc.
    useEffect(() => {
      return () => {
        sendManagedTeamsSummaryIfNeeded();
      };
    }, []); // Empty dependency array means it runs only on unmount

    // Add flag to control if the function is already executing
    const isExecutingRef = useRef(false);

    // Lock set to coordinate background recovery for common/super
    const activeCommonSuperRequests = useRef(new Set<string>());
    const lastCommonContextRef = useRef<{ platformId: string; leagueId: string; teamName: string } | null>(null);
    const lastSuperContextRef = useRef<{ platformId: string; leagueId: string; teamName: string } | null>(null);

    const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
    const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
    //extract authId from user auth0 data for cache key
    const authId = user?.sub;

    const currentSelectedPlatformIdRef = useRef<string | undefined>();
    // Track currently selected team for polling guard logic
    const currentTeamInfoRefTD = useRef<{ platformId: string; leagueId: string; teamName: string } | null>(null);
    useEffect(() => {
      if (selectedTeamInfo?.platformId && selectedTeamInfo?.leagueId && selectedTeamInfo?.teamName) {
        currentTeamInfoRefTD.current = {
          platformId: selectedTeamInfo.platformId,
          leagueId: selectedTeamInfo.leagueId,
          teamName: selectedTeamInfo.teamName,
        };
      } else {
        currentTeamInfoRefTD.current = null;
      }
    }, [selectedTeamInfo]);

    useEffect(() => {
      const newSelectedId = leaguePlatforms.find((p) => p.isSelected)?.id;
      currentSelectedPlatformIdRef.current = newSelectedId;
      console.log("LS: useEffect - currentSelectedPlatformIdRef.current update to:", newSelectedId);
    }, [leaguePlatforms]);

    // Sync ref during render too — useEffect alone lags one frame and lets stale Un-Synced handlePartnerClick apply
    currentSelectedPlatformIdRef.current = leaguePlatforms.find((p) => p.isSelected)?.id;

    // Update the isSuperAnalysisCompleteRef when isSuperAnalysisComplete changes
    useEffect(() => {
      isSuperAnalysisCompleteRef.current = isSuperAnalysisComplete;
    }, [isSuperAnalysisComplete]);

    // Helper function to sleep for a given number of milliseconds
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    // Function to wait for super analysis to complete
    const waitForSuperAnalysisToComplete = async (): Promise<void> => {
      const waitForStartMs = 2500; // 2.5 seconds timeout
      const waitForCompleteMs = 120000; // 120 seconds timeout
      const pollEveryMs = 150; // 150ms polling interval

      const startWindowStart = Date.now();
      let superStarted = false;

      // Wait briefly for this selection's super analysis to start (state flips to false).
      while (Date.now() - startWindowStart < waitForStartMs) {
        if (!isSuperAnalysisCompleteRef.current) {
          superStarted = true;
          break;
        }
        await sleep(pollEveryMs);
      }

      // If super analysis never started (e.g. cache/very fast path), don't block audio.
      if (!superStarted) return;

      const completeWindowStart = Date.now();
      while (Date.now() - completeWindowStart < waitForCompleteMs) {
        if (isSuperAnalysisCompleteRef.current) return;
        await sleep(pollEveryMs);
      }

      console.warn("[Funny Audio] Timed out waiting for super analysis completion. Proceeding with playback.");
    };

    const cleanAllSectionData = () => {
      // Clear the teams data to avoid stale data
      //reset the error message no draft
      // setTeamsdata(null);
      // setTeamsdata([]);

      // // Explicitly clear stream-specific states
      setWaiverStreamState({ status: "", thought: "", isGeneratingResult: false, isStreamActive: false, isWaitingForChart: false, isGeneratingAudio: false, teamKey: null });
      setStartStreamState({ status: "", thought: "", isGeneratingResult: false, isStreamActive: false, isWaitingForChart: false, isGeneratingAudio: false, teamKey: null });
      setRundownStreamState({ status: "", thought: "", isGeneratingResult: false, isStreamActive: false, isWaitingForChart: false, isGeneratingAudio: false, teamKey: null });
      setDraftAdviceStreamState({ status: "", thought: "", isGeneratingResult: false, isStreamActive: false, isWaitingForChart: false, isGeneratingAudio: false, teamKey: null });

      // // Explicitly clear the local data states in HomePage to prevent stale data from being displayed
      // // For stream-based sections, reset to initial object structure
      setWaiverData({ waiver_analysis: "", chart_data: [], request_id: null });
      setStartData({ start_analysis: "", chart_data: [], request_id: null });
      setRundownData({ rundown_analysis: "", chart_data: [], request_id: null });
      setDraftAdviceData({ rundown_analysis: "", chart_data: [], request_id: null });
      // For regular sections, reset to null
      setNewsData(null);
      setRostersData(null);
      setRankingsData(null);
      setAllRankingsData(null);
      // Activate all loadings at the beginning
      setLoadingNews(showNews);
      setLoadingWaiver(false);
      setLoadingStart(false);
      setLoadingRoster(showRosters);
      setLoadingRankings(showAllRankings);
      setLoadingAllRankings(showAllRankings);
      setLoadingRundown(false);
      setLoadingDraftAdvice(false);
      setHasLoadingStarted(true);

      setErrorMessageNoDraft({
        waiver: false,
        news: false,
        start: false,
        rankings: false,
        allRankings: false,
        rosters: false,
        rundown: false,
        DraftAdvice: false,
      });

      // CRITICAL: Reset commonLeagueDataFailed when team changes
      // This ensures that error state from previous team doesn't persist
      setCommonLeagueDataFailed(false);
      // After wiping section payloads, super analysis is not valid for this team until the new run finishes.
      // Prevents manual endpoints + Chat from appearing clickable for ~1s while stale isSuperAnalysisComplete was still true.
      setIsSuperAnalysisComplete(false);
    };

    const abortControllerRef = useRef<AbortController | null>(null);

    // Function to handle the partner click and execute the endpoints (cached or not)
    const handlePartnerClick = async (partner, leagueId, platformId: string, signal?: AbortSignal, forceRefresh = false, calculatorSettingsOverride?: any) => {
      // CRITICAL: Reset commonLeagueDataFailed when team changes
      // This ensures that error state from previous team doesn't persist
      setCommonLeagueDataFailed(false);
      setShowIdpDialog(false);
      setIsIdpLeagueBlocked(false);

      const stopIdpLeagueFlow = () => {
        setShowIdpDialog(true);
        setIsIdpLeagueBlocked(true);
        setLoadingNews(false);
        setLoadingWaiver(false);
        setLoadingStart(false);
        setLoadingRoster(false);
        setLoadingRankings(false);
        setLoadingAllRankings(false);
        setLoadingRundown(false);
        setLoadingDraftAdvice(false);
        setHasLoadingStarted(false);
        setIsSuperAnalysisComplete(true);
        setRefreshingLeagueData(false);
        setIsTransitioning(false);
        handlePartnerSelected(partner);
        handleTeamSelection(partner, leagueId);
        handleLeagueId(leagueId);
      };

      const currentPlatform = leaguePlatforms.find((p) => p.isSelected)?.id;
      // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
      const currentUserDataPlatform = userDataCache[platformId];

      // 🆕 NEW: ESPN session guard (primary: SWID/S2, fallback: 23:55 time-based via isEspnTokenExpiring)
      if (currentUserDataPlatform?.integration_name === "espn") {
        console.log("🔍 [TeamsDropdown] ESPN session guard triggered. Checking session validity...");
        const requiresReauth = shouldOpenEspnReauth(currentUserDataPlatform);
        if (requiresReauth) {
          console.log("🔴 [TeamsDropdown] ESPN session requires re-auth (primary: no SWID/S2, fallback: time-based expired). Opening login form.");
          setLoggingInTo("ESPN");
          setLoginError("Please connect again");

          // Clean up UI and stop loading states
          setLoadingNews(false);
          setLoadingWaiver(false);
          setLoadingStart(false);
          setLoadingRoster(false);
          setLoadingRankings(false);
          setLoadingRundown(false);
          setLoadingDraftAdvice(false);
          setHasLoadingStarted(false);

          return; // IMPORTANT: Stop execution here
        } else {
          console.log("✅ [TeamsDropdown] ESPN session valid. Proceeding with team selection.");
        }
      }

      // 🆕 AGREGAR COMPARACIÓN:
      if (currentUserDataPlatform?.integration_name !== userDataPlatform?.integration_name) {
        console.warn("⚠️ [RACE CONDITION DETECTED] in @teamsdropdown", {
          cache: currentUserDataPlatform?.integration_name,
          state: userDataPlatform?.integration_name,
          expectedPlatform: platformId,
        });
      }

      // --- Check cache first ---
      // Check if we have valid cache (only if not forceRefresh and not transitioning)
      // Un-Synced: only news + allRankings are fetched (blocked agents never populate cache)
      const hasRequiredCachedEndpoints = isUnsyncedPlatform(platformId)
        ? !!(sectionData.news && sectionData.allRankings)
        : !!(
          sectionData.waiver &&
          sectionData.start &&
          sectionData.rundown &&
          sectionData.DraftAdvice &&
          sectionData.news &&
          sectionData.rosters &&
          sectionData.rankings &&
          sectionData.allRankings
        );
      const hasValidCache =
        !forceRefresh && // if forceRefresh is true, we don't check the cache
        !isTransitioning && // check if there is a transition in progress to avoid re executing the function
        isCacheValid(sectionData, currentPlatform, partner, leagueId) &&
        hasRequiredCachedEndpoints;

      // 🆕 NEW: Activate refreshing state and update timestamp when NOT using cache
      // Only execute if: 1) NOT forceRefresh (manual refresh) AND 2) NO valid cache
      if (hasValidCache) {
        if (isIdpFromCommonLeagueData(sectionData.commonLeagueData)) {
          stopIdpLeagueFlow();
          return;
        }

        console.log("handlePartnerClick: All data found in cache. Restoring state.");
        // Restoring the UI from the cache
        // CRITICAL: Set fromCache: true when loading from cache to ensure correct button display
        setWaiverData(sectionData.waiver);
        setStartData(sectionData.start);
        setRundownData(sectionData.rundown);
        setDraftAdviceData(sectionData.DraftAdvice);
        setNewsData(sectionData.news);
        setRostersData(sectionData.rosters);
        setRankingsData(sectionData.rankings);
        setAllRankingsData(sectionData.allRankings);

        // Deactivate all loaders
        setLoadingNews(false);
        setLoadingWaiver(false);
        setLoadingStart(false);
        setLoadingRoster(false);
        setLoadingRankings(false);
        setLoadingAllRankings(false);
        setLoadingRundown(false);
        setLoadingDraftAdvice(false);
        setHasLoadingStarted(false); //   Indicates that the loading has finished
        // Ensure manual buttons do not revert to 'Analyzing...' when using cache
        setIsSuperAnalysisComplete(true);

        // Update the UI — skip if user already switched platform (stale Un-Synced auto-select)
        if (currentSelectedPlatformIdRef.current !== platformId) {
          console.log("handlePartnerClick: stale platform (cache path), skip apply", platformId);
          return;
        }
        handlePartnerSelected(partner);
        handleTeamSelection(partner, leagueId);
        handleLeagueId(leagueId);
        setMessage("Use the buttons below for quick detailed insights into your team");
        setDataLoadedFromTeamsPage(true);
        return; // CRITICAL: Exit the function to avoid re-fetching data.
      }

      // //  Start the transition and clean the UI ---
      // // CRITICAL: Set the transition to TRUE here, BEFORE doing anything else.
      // setIsTransitioning(true);

      // 🆕 NEW: Flag to track if we activated refreshing (for cleanup in finally)
      // let shouldDeactivateRefreshing = false;

      // let shouldDeactivateRefreshing = false;
      // let didRunRefreshBlock = false;
      // const runRefreshUIOnce = () => {
      //   if (didRunRefreshBlock || forceRefresh) return;
      //   setRefreshingLeagueData(true);
      //   shouldDeactivateRefreshing = true;
      //   setIsSuperAnalysisComplete(false);

      //   // timestamp
      //   const now = new Date();
      //   const date = `${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}-${now.getFullYear().toString().slice(-2)}`;
      //   const time = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`;
      //   const newTimestamp = `${date} ${time}`;
      //   const teamKey = `${platformId}-${leagueId}`;
      //   setLastUpdatedTimestamps((prev) => ({ ...prev, [teamKey]: newTimestamp }));

      //   didRunRefreshBlock = true;
      // };

      // 🆕 NEW: Activate refreshing state and update timestamp when NOT using cache
      // Only if forceRefresh is false (meaning it's a new team selection, not a manual refresh)
      let shouldDeactivateRefreshing = false;

      // if (!forceRefresh) {
      //   console.log("🚨 EXECUTANDO BLOQUE - common/super NO están cacheados");

      //   setRefreshingLeagueData(true);
      //   shouldDeactivateRefreshing = true;
      //   setIsSuperAnalysisComplete(false);

      //   // Update timestamp
      //   const now = new Date();
      //   const date = `${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}-${now.getFullYear().toString().slice(-2)}`;
      //   const time = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`;
      //   const newTimestamp = `${date} ${time}`;
      //   const teamKey = `${platformId}-${leagueId}`;
      //   setLastUpdatedTimestamps((prev) => ({
      //     ...prev,
      //     [teamKey]: newTimestamp,
      //   }));
      // }

      //  Start the transition and clean the UI ---
      // CRITICAL: Set the transition to TRUE here, BEFORE doing anything else.
      setIsTransitioning(true);

      // 2. Clean all the data while the sections are not visible.
      cleanAllSectionData();
      setEndpointErrors({});

      let taskId: string;
      if (Capacitor.isNativePlatform()) {
        try {
          taskId = await BackgroundTask.beforeExit(async () => {
            console.log("handlePartnerClick: Background task permission requested.");
          });
        } catch (e) {
          console.error("handlePartnerClick: Failed to start background task", e);
        }
      }

      try {
        // const currentPlatform = leaguePlatforms.find((p) => p.isSelected)?.id;
        // If the team has changed, update preferences BEFORE executing the endpoints
        if (newClickOnPlatofrm) {
          try {
            setIsChangingPlatform(true);

            // Update selectedTeams before calling postUserPreferences
            const updatedSelectedTeams = {
              ...selectedTeams,
              [platformId]: {
                platformId: platformId,
                teamName: partner,
                leagueId: leagueId,
              },
            };

            setSelectedTeams(updatedSelectedTeams);

            const accessAuthToken = await getSafeAccessAuth0Token();

            if (!accessAuthToken) {
              return;
            }

            // Backend REPLACES these fields (no merge): always send the full per-platform maps
            // and every field, otherwise other platforms and the settings maps are wiped.
            await postUserPreferences(accessAuthToken, {
              loggedPlatforms: leaguePlatforms,
              teamsData: {
                ...teamsDataCache,
                [platformId]: teamsData?.leagues?.length > 0 ? teamsData : teamsDataCache?.[platformId],
              },
              userDataPlatform: userDataCache,
              selectedTeams: updatedSelectedTeams,
              lastUpdatedTimestamps,
              calculatorSettingsMap,
              keeperDrawerSeenMap,
              aiSettings,
            });

            setIsChangingPlatform(false);
          } catch (error) {
            console.error("Error updating preferences before endpoints:", error);
            setIsChangingPlatform(false);
          }
        }

        // setIsTransitioning(true);
        // Reset returnedFromBackground when starting a new load
        // setReturnedFromBackground(false);

        // Prefer local teamsData prop; fall back to cache for the SAME platformId only
        // (Capacitor cold start: setTeamsdata may not have committed before handlePartnerClick).
        // Never read another platform's leagues — avoids cross-platform race.
        const platformTeamsForSelect =
          teamsData?.leagues?.length > 0 ? teamsData : teamsDataCache?.[platformId];

        // Check if the team exists in teamsData before proceeding
        if (!platformTeamsForSelect || !platformTeamsForSelect.leagues) {
          console.log("Team data not yet available, skipping request");
          setIsTransitioning(false); // Reset transitioning if we abort early
          return;
        }
        const teamExists = platformTeamsForSelect.leagues.some((team) => team.league_id === leagueId && team.name === partner);

        // If the team does not exist, skip the request
        if (!teamExists) {
          console.log("Team data not yet available, skipping request");
          setIsTransitioning(false); // Reset transitioning if we abort early
          return;
        }

        if (isExecutingRef.current) {
          // If there's an execution in progress, cancel it before starting the new one
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
          }
          // Cancel pending requests from the context
          cancelPendingRequests();
        }

        // Set the flag to true to indicate that the function is executing
        isExecutingRef.current = true;

        try {
          // Re-resolve after await gaps using the same platformId-scoped source
          const platformTeamsInner =
            teamsData?.leagues?.length > 0 ? teamsData : teamsDataCache?.[platformId];
          // Check if the team exists in teamsData before proceeding
          if (!platformTeamsInner || !platformTeamsInner.leagues) {
            console.log("Team data not yet available, skipping request");
            return;
          }
          const teamExistsInner = platformTeamsInner.leagues.some((team) => team.league_id === leagueId && team.name === partner);

          // If the team does not exist, skip the request
          if (!teamExistsInner) {
            console.log("Team data not yet available, skipping request");
            return;
          }

          // Cancel pending previous requests
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          cancelPendingRequests(); // Cancel also the requests from the context
          const controller = new AbortController();
          abortControllerRef.current = controller;
          setCurrentAbortController(controller);
          const signal = controller.signal;

          // Close accordion only on manual team click (see handleTeamSelectionClick).
          // Auto-restore / refresh call handlePartnerClick and must NOT collapse panel1.
          // if (expanded) {
          //   setExpanded("");
          // }

          // Reset states — skip if user already switched platform (stale Un-Synced auto-select)
          if (currentSelectedPlatformIdRef.current !== platformId) {
            console.log("handlePartnerClick: stale platform (fetch path), skip apply", platformId);
            setIsTransitioning(false);
            return;
          }
          setDataLoadedFromTeamsPage(false);
          handlePartnerSelected(partner);
          setPartnerClicked(true);

          handleTeamSelection(partner, leagueId);
          handleLeagueId(leagueId);
          // Sync before any await so INCOMPLETE polling sees this league (useEffect lags, and teamName was missing).
          currentTeamInfoRefTD.current = { platformId: currentPlatform || platformId, leagueId, teamName: partner };

          // Activate all loadings at the beginning
          setLoadingNews(showNews);
          // setLoadingWaiver(true);
          // setLoadingStart(true);
          setLoadingRoster(showRosters);
          setLoadingRankings(showAllRankings);
          setLoadingAllRankings(showAllRankings);
          // setLoadingRundown(true);
          setHasLoadingStarted(true);

          // Reset errors to false before starting the new request
          setEndpointErrors({});

          setMessage("Please wait while we load your information");

          // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
          const currentUserDataPlatform = userDataCache[platformId];
          // NEW: Add validToken to the context
          let validToken = currentUserDataPlatform?.token;
          //  If the platform is Yahoo, verify and refresh the token if necessary
          if (currentUserDataPlatform.integration_name === "yahoo") {
            try {
              console.log("🔄 TeamsDropdown handlePartnerClick triggered:", {
                integrationName: currentUserDataPlatform?.integration_name,
                userDataPlatformToken: currentUserDataPlatform?.token,
                yahooToken: yahooToken?.substring(0, 10) + "...",
                timestamp: new Date().toISOString(),
              });

              // 🆕 NEW: Get valid token here instead of useEffect
              console.log("🔄 Getting valid Yahoo token in handlePartnerClick...");
              const yahooTokenRefreshed = await getValidYahooToken();

              if (yahooTokenRefreshed) {
                validToken = yahooTokenRefreshed;
                // setValidToken(yahooTokenRefreshed);
                console.log("✅ Valid token obtained in handlePartnerClick:", {
                  validToken: yahooTokenRefreshed?.substring(0, 10) + "...",
                  timestamp: new Date().toISOString(),
                });
                console.log("✅ Valid token obtained:", yahooTokenRefreshed.substring(0, 10) + "...");
              } else {
                console.log("❌ No valid Yahoo token in handlePartnerClick:", {
                  integrationName: currentUserDataPlatform?.integration_name,
                  timestamp: new Date().toISOString(),
                });
                // setValidToken(null);
                validToken = null;
                setLoggingInTo("Yahoo");
                console.log("OPEN LOGIN FORM FOR TEAMS DROPDOWN.TSX");
                setLoginError("Please connect again");

                return; // 🚨 STOP EXECUTION HERE
              }

              console.log("validToken FIRST LOG", yahooTokenRefreshed?.substring(0, 10) + "...");
              console.log("yahooToken FIRST LOG", yahooToken?.substring(0, 10) + "...");

              // Reactivate the loadings after getting the token
              setLoadingNews(showNews);
              // setLoadingWaiver(true);
              // setLoadingStart(true);
              setLoadingRoster(showRosters);
              setLoadingRankings(showAllRankings);
              setLoadingAllRankings(showAllRankings);
              // setLoadingRundown(true);
            } catch (error) {
              console.error("❌ Error getting valid Yahoo token in handlePartnerClick:", error);
              // setValidToken(null);
              validToken = null;
              setLoggingInTo("Yahoo");
              setLoginError("Please connect again");

              return; // 🚨 STOP EXECUTION HERE
            }
          }

          const accessAuthToken = await getSafeAccessAuth0Token();
          //guard clause to stop execution if the token is not valid (log out)
          if (!accessAuthToken) {
            return;
          }

          // // // If there is no valid cache or missing data, execute the endpoints
          // const commonLeagueData = await getCommonLeagueData(authId, userDataPlatform, leagueId, pocToken, accessAuthToken, signal, validToken);
          // // setIsYahooLoading(false);
          // // add timestamp to commonLeagueData
          // const commonLeagueDataWithTimestamp = {
          //   ...commonLeagueData,
          //   timestamp: new Date().toISOString(),
          //   platform: currentPlatform,
          //   teamName: partner,
          //   leagueId: leagueId,
          // };
          // updateSectionData("commonLeagueData", commonLeagueDataWithTimestamp);
          // setCommonLeagueLeagueType(commonLeagueData.league_info.league_type); //this value is for trade analysis body

          const processCommonDataStream = async (onThought: (thought: string, onFreshHint?: () => void, onFromCache?: (v: boolean) => void) => void): Promise<any> => {
            const startTime = Date.now();
            // Build requestKey and context for locks/polling
            const requestKey = `${authId}-common-${currentPlatform}-${leagueId}`;
            lastCommonContextRef.current = { platformId: currentPlatform, leagueId, teamName: partner };

            // Use calculatorSettingsOverride if provided (from handleRefreshEndpoints), otherwise use prop
            // Define this outside try-catch so it's available in catch block for polling fallback
            const settingsToUse = calculatorSettingsOverride !== undefined ? calculatorSettingsOverride : calculatorSettings;

            // Add lock at start so recovery can pick it up even if stream is cut by iOS
            activeCommonSuperRequests.current.add(requestKey);
            // Polled payload for this league only (sectionData in this closure can still be the previous team).
            let polledCommon: any = null;

            try {
              const response = await getCommonLeagueDataStream(
                currentUserDataPlatform,
                leagueId,
                pocToken,
                accessAuthToken,
                signal,
                validToken,
                forceRefresh,
                settingsToUse,
                superAnalysisModel,
                platformId,
              );

              // Reset leagueSettingsChanged flag after sending calculator_settings
              if (settingsToUse && setLeagueSettingsChanged) {
                setLeagueSettingsChanged(false);
              }

              //get estimated times from backend when common data stream is started
              loadEstimatedTimes();

              // If backend signals INCOMPLETE, switch to polling
              if (response === "INCOMPLETE") {
                await startPollingForCompletion({
                  endpointType: "common",
                  sectionKey: "commonLeagueData",
                  leagueId,
                  teamName: partner,
                  yahooToken: validToken,
                  signal: undefined,
                  context: { platformId: currentPlatform, leagueId, userId: authId },
                  getAccessTokenSilently: getSafeAccessAuth0Token,
                  authId,
                  currentUserDataPlatform,
                  pocToken,
                  apiCall: (_auth, udp, lg, _tm, poc, token, sig, ytok, apiCallArgs) => {
                    const calcSettings = apiCallArgs?.calculatorSettings;
                    return getCommonLeagueDataStream(udp, lg, poc, token, sig, ytok, undefined, calcSettings, superAnalysisModel, platformId);
                  },
                  apiCallArgs: { calculatorSettings: settingsToUse },
                  setStreamState: () => { },
                  setData: () => { },
                  setLoading: () => { },
                  updateSectionData: (_section, data) => {
                    const withMeta = { ...data, timestamp: new Date().toISOString(), platform: currentPlatform, teamName: partner, leagueId };
                    updateSectionData("commonLeagueData", withMeta);
                  },
                  setEndpointErrors: () => { },
                  setErrorMessages: setErrorMessages,
                  setHasAnimationCompleted: () => { },
                  requestKey,
                  activeStreamRequests: activeCommonSuperRequests,
                  retrievingMessage: "Retrieving your league data...",
                  errorKey: "League Data",
                  currentTeamInfoRef: currentTeamInfoRefTD as any,
                  setThoughtsCommon,
                  onComplete: (finalData) => {
                    polledCommon = finalData;
                    try {
                      if (finalData?.league_info?.league_type) {
                        setCommonLeagueLeagueType(finalData.league_info.league_type);
                      }
                    } catch { }
                  },
                });
                // Prefer this poll's payload. sectionData here is the closure from click-start and can be another league.
                if (polledCommon?.league_info) return polledCommon;
                const commonFromCache = sectionData?.commonLeagueData;
                if (
                  commonFromCache?.league_info &&
                  commonFromCache.leagueId === leagueId &&
                  commonFromCache.platform === currentPlatform
                ) {
                  return commonFromCache;
                }
                return null; // safe fallback - polling will update sectionData
              }

              if (!response || typeof response === "string" || !(response as Response).body) {
                throw new Error("Failed to get common league data stream.");
              }
              // Type guard: ensure response is Response type, not a different string.
              // if (!response) {
              //   if (response === undefined) return; // Aborted, error handled in catch block
              //   throw new Error("Streaming request failed");
              // } else if (typeof response === "string") {
              //   // This should not happen now due to the lock, but as a safeguard:
              //   throw new Error(`Unexpected error response`);
              // } else if (!response.body) {
              //   throw new Error("Streaming request failed - no body");
              // }

              const reader = (response as Response).body!.getReader();

              const decoder = new TextDecoder();
              let finalData = null;
              let commonFromRedisCache = false;
              let buffer = "";
              // let thoughtCount = 0; // Contador para controlar el timing

              // Read the stream
              while (true) {
                const { done, value } = await reader.read();
                if (done) {
                  break;
                }

                buffer += decoder.decode(value, { stream: true });
                const messages = buffer.split("\n\n");
                buffer = messages.pop() || ""; // Keep any incomplete message in the buffer

                for (const message of messages) {
                  if (!message.startsWith("data: ")) continue;

                  let event;
                  try {
                    const jsonData = message.substring(6);
                    event = JSON.parse(jsonData);
                  } catch (e) {
                    console.error("Failed to parse JSON from stream:", e, message);
                    continue; // Skip this malformed message
                  }

                  // The `event` object is now like { type: "thought", payload: "..." }
                  switch (event.type) {
                    case "thought":
                      // if common data start without cache, we need to start the loaders
                      if (!forceRefresh) {
                        console.log("🚨 Executing block COMMON DATA - for start loaders");

                        setRefreshingLeagueData(true);
                        shouldDeactivateRefreshing = true;
                        setIsSuperAnalysisComplete(false);
                      }
                      // setThoughtsCommon(event.payload);
                      // // Añadir retardo de 5 segundos entre pensamientos
                      // await new Promise((resolve) => setTimeout(resolve, 5000));
                      onThought(event.payload);
                      // thoughtCount++;
                      break;
                    case "data": // This is what the network tab shows
                      // console.log(`[STREAM DATA] Received ${event.type} payload:`, event.payload);
                      finalData = event.payload; // Assign the PAYLOAD to finalData
                      if (event.payload?.fromCache) commonFromRedisCache = true;
                      break;
                    case "api_error":
                      throw new Error(event.payload);
                    case "complete":
                      // This event just signals the end, we can ignore it.
                      if (event?.payload?.fromCache) commonFromRedisCache = true;
                      break;
                  }
                }
              }

              const duration = Date.now() - startTime;
              const totalSeconds = Math.floor(duration / 1000);
              const minutes = Math.floor(totalSeconds / 60);
              const seconds = totalSeconds % 60;
              const formattedTime = minutes > 0 ? `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")} m` : `${String(seconds).padStart(2, "0")} s`;
              console.log(`[PERF] processCommonDataStream: Finished receiving stream from chat-backend in ${formattedTime}.`);
              if (!commonFromRedisCache) {
                void trackEndpointResponseTime(authId || "unknown", "common_league_data", duration / 1000, currentPlatform, leagueId);
              }

              if (!finalData) {
                console.log("[STREAM DATA] Stream ended without providing common league data.");
                throw new Error("Stream ended without providing common league data.");
              }

              return finalData;
            } catch (error) {
              if (error.name === "AbortError") {
                // If user switched teams/platforms, don't recover previous request
                const ctx = lastCommonContextRef.current;
                if (!ctx || !selectedTeamInfo || ctx.platformId !== selectedTeamInfo.platformId || ctx.leagueId !== selectedTeamInfo.leagueId) {
                  throw error; // let outer catch handle as cancel
                }
              }
              if (error.name === "AbortError" || error.name === "TypeError") {
                await startPollingForCompletion({
                  endpointType: "common",
                  sectionKey: "commonLeagueData",
                  leagueId,
                  teamName: partner,
                  yahooToken: validToken,
                  signal: undefined,
                  context: { platformId: currentPlatform, leagueId, userId: authId },
                  getAccessTokenSilently: getSafeAccessAuth0Token,
                  authId,
                  currentUserDataPlatform,
                  pocToken,
                  apiCall: (_auth, udp, lg, _tm, poc, token, sig, ytok, apiCallArgs) => {
                    const calcSettings = apiCallArgs?.calculatorSettings;
                    return getCommonLeagueDataStream(udp, lg, poc, token, sig, ytok, undefined, calcSettings, superAnalysisModel, platformId);
                  },
                  apiCallArgs: { calculatorSettings: settingsToUse },
                  setStreamState: () => { },
                  setData: () => { },
                  setLoading: () => { },
                  updateSectionData: (_section, data) => {
                    const withMeta = { ...data, timestamp: new Date().toISOString(), platform: currentPlatform, teamName: partner, leagueId };
                    updateSectionData("commonLeagueData", withMeta);
                  },
                  setEndpointErrors: () => { },
                  setErrorMessages: setErrorMessages,
                  setHasAnimationCompleted: () => { },
                  requestKey,
                  activeStreamRequests: activeCommonSuperRequests,
                  retrievingMessage: "Retrieving your league data...",
                  errorKey: "League Data",
                  currentTeamInfoRef: currentTeamInfoRefTD as any,
                  setThoughtsCommon,
                  onComplete: (finalData) => {
                    polledCommon = finalData;
                    try {
                      if (finalData?.league_info?.league_type) {
                        setCommonLeagueLeagueType(finalData.league_info.league_type);
                      }
                    } catch { }
                  },
                });
                activeCommonSuperRequests.current.delete(requestKey);
                if (polledCommon?.league_info) return polledCommon;
                const commonFromCache = sectionData?.commonLeagueData;
                if (
                  commonFromCache?.league_info &&
                  commonFromCache.leagueId === leagueId &&
                  commonFromCache.platform === currentPlatform
                ) {
                  return commonFromCache;
                }
                return null; // safe fallback - polling will update sectionData
              }
              throw error;
            }
          };

          // Holds league_type from this request's stream (sectionData can still be the previous team until React re-renders).
          let resolvedCommonLeagueDataForType: { league_info?: { league_type?: string; league_status?: string } } | null = null;
          try {
            const commonLeagueData = await processCommonDataStream(setThoughtsCommon);
            resolvedCommonLeagueDataForType = commonLeagueData ?? null;
            // setIsYahooLoading(false);
            // Only if we have valid data, write to section and set league_type
            if (commonLeagueData && commonLeagueData.league_info) {
              // add timestamp to commonLeagueData
              const commonLeagueDataWithTimestamp = {
                ...commonLeagueData,
                timestamp: new Date().toISOString(),
                platform: currentPlatform,
                teamName: partner,
                leagueId: leagueId,
              };
              updateSectionData("commonLeagueData", commonLeagueDataWithTimestamp);
              setCommonLeagueLeagueType(commonLeagueData.league_info.league_type); //this value is for trade analysis body
            } else {
              // Common still in flight or poll returned another league's cache. Do not start super/rosters on that body.
              console.log("[TeamsDropdown] Common data not ready yet (polling), skipping dependent endpoints.");
              setRefreshingLeagueData(false);
              setIsSuperAnalysisComplete(true);
              return;
            }

            const isIdpFromCommonData = isIdpFromCommonLeagueData(
              commonLeagueData ?? sectionData?.commonLeagueData
            );
            if (isIdpFromCommonData) {
              stopIdpLeagueFlow();
              return;
            }
          } finally {
            const key = `${authId}-common-${currentPlatform}-${leagueId}`;
            activeCommonSuperRequests.current.delete(key);
          }

          // await new Promise((resolve) => setTimeout(resolve, 500));
          // NEW: Process Super Analysis Stream right after Common Data
          const processSuperAnalysisStream = async (onThought: (thought: string) => void): Promise<any> => {
            const startTime = Date.now();
            // Build requestKey/context for locks/polling
            const requestKey = `${authId}-super-${currentPlatform}-${leagueId}`;
            lastSuperContextRef.current = { platformId: currentPlatform, leagueId, teamName: partner };

            // Use calculatorSettingsOverride if provided (from handleRefreshEndpoints), otherwise use prop
            // Define this outside try-catch so it's available in catch block for polling fallback
            const settingsToUse = calculatorSettingsOverride !== undefined ? calculatorSettingsOverride : calculatorSettings;

            // Add lock at start
            activeCommonSuperRequests.current.add(requestKey);

            try {
              const response = await getSuperAnalysisStream(
                authId,
                currentUserDataPlatform,
                leagueId,
                partner, // teamName
                pocToken,
                accessAuthToken,
                signal,
                validToken,
                forceRefresh,
                settingsToUse,
                superAnalysisModel,
                platformId,
              );

              if (response === "INCOMPLETE") {
                activeCommonSuperRequests.current.add(requestKey);
                await startPollingForCompletion({
                  endpointType: "super",
                  sectionKey: "super",
                  leagueId,
                  teamName: partner,
                  yahooToken: validToken,
                  signal: undefined,
                  context: { platformId: currentPlatform, leagueId, userId: authId },
                  getAccessTokenSilently: getSafeAccessAuth0Token,
                  authId,
                  currentUserDataPlatform,
                  pocToken,
                  apiCall: (_auth, udp, lg, _tm, poc, token, sig, ytok, apiCallArgs) => {
                    const calcSettings = apiCallArgs?.calculatorSettings;
                    return getSuperAnalysisStream(_auth, udp, lg, _tm, poc, token, sig, ytok, undefined, calcSettings, superAnalysisModel, platformId);
                  },
                  apiCallArgs: { calculatorSettings: settingsToUse },
                  setStreamState: () => { },
                  setData: () => { },
                  setLoading: () => { },
                  updateSectionData: () => { },
                  setEndpointErrors: () => { },
                  setErrorMessages: setErrorMessages,
                  setHasAnimationCompleted: () => { },
                  requestKey,
                  activeStreamRequests: activeCommonSuperRequests,
                  retrievingMessage: "Retrieving your super analysis...",
                  errorKey: "League Data",
                  currentTeamInfoRef: currentTeamInfoRefTD as any,
                  setThoughtsCommon,
                });
                // We don't rely on section cache for super; gating is enough
                return null; // safe fallback - polling will handle completion
              }

              if (!response || typeof response === "string" || !(response as Response).body) {
                throw new Error("Failed to get super analysis stream.");
              }

              const reader = (response as Response).body!.getReader();
              const decoder = new TextDecoder();
              let finalData = null;
              let superFromRedisCache = false;
              let buffer = "";
              // let thoughtCount = 0;

              while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const messages = buffer.split("\n\n");
                buffer = messages.pop() || "";

                for (const message of messages) {
                  if (!message.startsWith("data: ")) continue;
                  let event;
                  try {
                    const jsonData = message.substring(6);
                    event = JSON.parse(jsonData);
                  } catch (e) {
                    console.error("Failed to parse JSON from super analysis stream:", e, message);
                    continue;
                  }

                  switch (event.type) {
                    case "thought":
                      // To keep it simple, I'm reusing the existing thoughts state.
                      // This can be changed to a new state if you want to display them separately.
                      // setThoughtsCommon(event.payload);
                      // add delay of 5 seconds between thoughts
                      // await new Promise((resolve) => setTimeout(resolve, 5000));
                      onThought(event.payload);
                      // thoughtCount++;
                      break;
                    case "data":
                      finalData = event.payload;
                      if (event.payload?.fromCache) superFromRedisCache = true;
                      break;
                    case "api_error":
                      throw new Error(event.payload);
                    case "complete":
                      console.log("🚨 super stream complete event?.payload", event?.payload);
                      // if super analysis finish without cache, we need to register the timestamp and deactivate the loaders
                      const fromCache = Boolean(event?.payload?.fromCache);
                      if (fromCache) superFromRedisCache = true;
                      if (!forceRefresh && !fromCache) {
                        console.log("🚨 Executing block - super is finished without cache");
                        setRefreshingLeagueData(true);
                        shouldDeactivateRefreshing = true;
                        setIsSuperAnalysisComplete(false);

                        // Update timestamp
                        const now = new Date();
                        const date = `${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}-${now.getFullYear().toString().slice(-2)}`;
                        const time = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now
                          .getSeconds()
                          .toString()
                          .padStart(2, "0")}`;
                        const newTimestamp = `${date} ${time}`;
                        const teamKey = `${platformId}-${leagueId}`;
                        setLastUpdatedTimestamps((prev) => ({
                          ...prev,
                          [teamKey]: newTimestamp,
                        }));
                      }
                      break;
                  }
                }
              }
              const duration = Date.now() - startTime;
              const totalSeconds = Math.floor(duration / 1000);
              const minutes = Math.floor(totalSeconds / 60);
              const seconds = totalSeconds % 60;
              const formattedTime = minutes > 0 ? `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")} m` : `${String(seconds).padStart(2, "0")} s`;
              console.log(`[PERF] processSuperAnalysisStream: Finished receiving stream from chat-backend in ${formattedTime}.`);
              if (!superFromRedisCache) {
                void trackEndpointResponseTime(authId || "unknown", "super_analysis", duration / 1000, currentPlatform, leagueId);
              }
              console.log("[STREAM DATA] Super Analysis stream finished.");
              return finalData;
            } catch (error) {
              // Rate limit: open drawer and resolve so no uncaught error is thrown
              if ((error as any).isRateLimit) {
                openRateLimitDrawer?.((error as any).retryAfter);
                activeCommonSuperRequests.current.delete(requestKey);
                return null;
              }
              if (error.name === "AbortError") {
                const ctx = lastSuperContextRef.current;
                if (!ctx || !selectedTeamInfo || ctx.platformId !== selectedTeamInfo.platformId || ctx.leagueId !== selectedTeamInfo.leagueId) {
                  throw error;
                }
              }
              if (error.name === "AbortError" || error.name === "TypeError") {
                await startPollingForCompletion({
                  endpointType: "super",
                  sectionKey: "super",
                  leagueId,
                  teamName: partner,
                  yahooToken: validToken,
                  signal: undefined,
                  context: { platformId: currentPlatform, leagueId, userId: authId },
                  getAccessTokenSilently: getSafeAccessAuth0Token,
                  authId,
                  currentUserDataPlatform,
                  pocToken,
                  apiCall: (_auth, udp, lg, _tm, poc, token, sig, ytok, apiCallArgs) => {
                    const calcSettings = apiCallArgs?.calculatorSettings;
                    return getSuperAnalysisStream(_auth, udp, lg, _tm, poc, token, sig, ytok, undefined, calcSettings, superAnalysisModel, platformId);
                  },
                  apiCallArgs: { calculatorSettings: settingsToUse },
                  setStreamState: () => { },
                  setData: () => { },
                  setLoading: () => { },
                  updateSectionData: () => { },
                  setEndpointErrors: () => { },
                  setErrorMessages: setErrorMessages,
                  setHasAnimationCompleted: () => { },
                  requestKey,
                  activeStreamRequests: activeCommonSuperRequests,
                  retrievingMessage: "Retrieving your super analysis...",
                  errorKey: "League Data",
                  currentTeamInfoRef: currentTeamInfoRefTD as any,
                  setThoughtsCommon,
                });
                activeCommonSuperRequests.current.delete(requestKey);
                return null; // safe fallback - polling will handle completion
              }
              throw error;
            } finally {
              // Ensure super lock is always released (success, error or polling path)
              activeCommonSuperRequests.current.delete(requestKey);
            }
          };


          // Redraft league type: only Draft Advice is disabled (other buttons behave normally). Match API casing/whitespace.
          // Prefer stream payload over sectionData so Draft Advice / auto-process is not stuck on the previous team's league_type.
          const leagueTypeContent =
            resolvedCommonLeagueDataForType?.league_info?.league_type ??
            sectionData?.commonLeagueData?.league_info?.league_type;
          const isLeagueTypeRedraft = typeof leagueTypeContent === "string" && leagueTypeContent.trim().toLowerCase() === "redraft";
          const leagueStatusFromCommon =
            resolvedCommonLeagueDataForType?.league_info?.league_status ??
            sectionData?.commonLeagueData?.league_info?.league_status;
          const canRunDraftAdvice =
            showDraftAdvice &&
            // !isLeagueTypeRedraft && // hide on porpuse to avoid blocking draft advice on redraft leagues
            !isDraftAdviceDisabledForPlatform(platformId, disabledPlatformDraftAdvice) &&
            !isDraftAdviceDisabledByLeagueStatus(leagueStatusFromCommon);

          // --- START the Super Analysis but DO NOT wait for it here ---
          // This allows other non-dependent tasks to start immediately.
          const superAnalysisPromise = processSuperAnalysisStream(setThoughtsCommon);
          // //--------------------------------
          //2. First Promise.all: WaiverSuggestions, Roster, News, Rankings
          // This now waits for both common_league_data and super_analysis to finish
          await Promise.all([
            // rosters fetching data
            (() => {

              // Un-Synced: Rosters is blocked until a real league is synced
              if (!showRosters || (isUnsyncedPlatform(platformId) && isUnsyncedBlockedAgent("Rosters"))) return Promise.resolve();
              const isNewRequest =
                !activeRequests.current.trades.isLoading || activeRequests.current.trades.platformId !== currentPlatform || activeRequests.current.trades.leagueId !== leagueId;
              if (isNewRequest) {
                activeRequests.current.trades = {
                  isLoading: true,
                  platformId: currentPlatform,
                  teamId: partner,
                  leagueId: leagueId,
                };
                // Use calculatorSettingsOverride if provided (from handleRefreshEndpoints), otherwise use prop
                const settingsToUse = calculatorSettingsOverride !== undefined ? calculatorSettingsOverride : calculatorSettings;
                return fetchRostersData(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse)
                  .catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, Rosters: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        const errorKey = `rosters-${platformId}-${leagueId}`;
                        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
                      }
                    }
                  })
                  .finally(() => {
                    setLoadingRoster(false);
                    activeRequests.current.trades.isLoading = false;
                  });
              }
              return Promise.resolve();
            })(),
            //news fetching data
            (() => {
              if (!showNews) return Promise.resolve();
              const isNewRequest =
                !activeRequests.current.news.isLoading || activeRequests.current.news.platformId !== currentPlatform || activeRequests.current.news.leagueId !== leagueId;
              if (isNewRequest) {
                activeRequests.current.news = {
                  isLoading: true,
                  platformId: currentPlatform,
                  teamId: partner,
                  leagueId: leagueId,
                };
                // Use calculatorSettingsOverride if provided (from handleRefreshEndpoints), otherwise use prop
                const settingsToUse = calculatorSettingsOverride !== undefined ? calculatorSettingsOverride : calculatorSettings;
                return fetchNewsData(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse)
                  .catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, News: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        const errorKey = `news-${platformId}-${leagueId}`;
                        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
                      }
                    }
                  })
                  .finally(() => {
                    setLoadingNews(false);
                    activeRequests.current.news.isLoading = false;
                  });
              }
              return Promise.resolve();
            })(),
            // // //rankings fetching data
            // (() => {
            //   const isNewRequest =
            //     !activeRequests.current.rankings.isLoading ||
            //     activeRequests.current.rankings.platformId !== currentPlatform ||
            //     activeRequests.current.rankings.leagueId !== leagueId;
            //   if (isNewRequest) {
            //     activeRequests.current.rankings = {
            //       isLoading: true,
            //       platformId: currentPlatform,
            //       teamId: partner,
            //       leagueId: leagueId,
            //     };
            //     // Use calculatorSettingsOverride if provided (from handleRefreshEndpoints), otherwise use prop
            //     const settingsToUse = calculatorSettingsOverride !== undefined ? calculatorSettingsOverride : calculatorSettings;
            //     return fetchRankingsData(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse)
            //       .catch((error) => {
            //         if (error.name === "AbortError") throw error;
            //         if (!returnedFromBackground) {
            //           const errorMessage = error?.message || "";
            //           setErrorMessages((prev) => ({ ...prev, Rankings: errorMessage }));
            //           if (errorMessage.trim() !== noDraftErrorMessage) {
            //             const errorKey = `rankings-${platformId}-${leagueId}`;
            //             setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
            //           }
            //         }
            //       })
            //       .finally(() => {
            //         setLoadingRankings(false);
            //         activeRequests.current.rankings.isLoading = false;
            //       });
            //   }
            //   return Promise.resolve();
            // })(),
            // // //all rankings fetching data
            (() => {
              if (!showAllRankings) return Promise.resolve();
              const isNewRequest =
                !activeRequests.current.allRankings?.isLoading ||
                activeRequests.current.allRankings?.platformId !== currentPlatform ||
                activeRequests.current.allRankings?.leagueId !== leagueId;
              if (isNewRequest) {
                if (!activeRequests.current.allRankings) {
                  activeRequests.current.allRankings = {
                    isLoading: false,
                    platformId: null,
                    teamId: null,
                    leagueId: null,
                  };
                }
                activeRequests.current.allRankings = {
                  isLoading: true,
                  platformId: currentPlatform,
                  teamId: partner,
                  leagueId: leagueId,
                };
                // Use calculatorSettingsOverride if provided (from handleRefreshEndpoints), otherwise use prop
                const settingsToUse = calculatorSettingsOverride !== undefined ? calculatorSettingsOverride : calculatorSettings;
                return fetchAllRankingsData(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse)
                  .catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, AllRankings: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        const errorKey = `allRankings-${platformId}-${leagueId}`;
                        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
                      }
                    }
                  })
                  .finally(() => {
                    setLoadingAllRankings(false);
                    activeRequests.current.allRankings.isLoading = false;
                  });
              }
              return Promise.resolve();
            })(),
            // // //waiver fetching data
            /* (async () => {
              await superAnalysisPromise; // This promise will now wait here

              const isNewRequest =
                !activeRequests.current.waiver.isLoading || activeRequests.current.waiver.platformId !== currentPlatform || activeRequests.current.waiver.leagueId !== leagueId;

              if (isNewRequest) {
                activeRequests.current.waiver = {
                  isLoading: true,
                  platformId: currentPlatform,
                  teamId: partner,
                  leagueId: leagueId,
                };

                console.log("🔍 TeamsDropdown - fetchWaiverDataStream call:", {
                  userDataPlatform: currentUserDataPlatform ? "EXISTS" : "NULL",
                  userDataPlatformIntegrationName: currentUserDataPlatform?.integration_name,
                  userDataPlatformToken: currentUserDataPlatform?.token,
                  userDataPlatformUserId: currentUserDataPlatform?.user_id,
                  validToken: validToken ? "EXISTS" : "NULL",
                  leagueId,
                  platformId,
                  partner,
                });
                // return fetchWaiverData(leagueId, signal, yahooToken)
                return fetchWaiverDataStream(leagueId, platformId, partner, signal, validToken,forceRefresh)
                  .catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, Waiver: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        const errorKey = `waiver-${platformId}-${leagueId}`;
                        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
                      }
                    }
                  })
                  .finally(() => {
                    // setLoadingWaiver(false) is handled inside fetchWaiverDataStream
                    activeRequests.current.waiver.isLoading = false;
                  });
              }
              return Promise.resolve();
            })() */
            // // rundown fetching data
            /* (async () => {
              await superAnalysisPromise; // This promise will now wait here

              const isNewRequest =
                !activeRequests.current.rundown.isLoading || activeRequests.current.rundown.platformId !== currentPlatform || activeRequests.current.rundown.leagueId !== leagueId;

              if (isNewRequest) {
                activeRequests.current.rundown = {
                  isLoading: true,
                  platformId: currentPlatform,
                  teamId: partner,
                  leagueId: leagueId,
                };

                return fetchRundownDataStream(leagueId, platformId, partner, signal, validToken,forceRefresh)
                  .catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, Rundown: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        const errorKey = `rundown-${platformId}-${leagueId}`;
                        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
                      }
                    }
                  })
                  .finally(() => {
                    activeRequests.current.rundown.isLoading = false;
                  });
              }

              return Promise.resolve();
            })() */
            // // //start fetching data
            /* (async () => {
              await superAnalysisPromise; // This promise will now wait here

              const isNewRequest =
                !activeRequests.current.start.isLoading || activeRequests.current.start.platformId !== currentPlatform || activeRequests.current.start.leagueId !== leagueId;

              if (isNewRequest) {
                activeRequests.current.start = {
                  isLoading: true,
                  platformId: currentPlatform,
                  teamId: partner,
                  leagueId: leagueId,
                };

                return fetchStartDataStream(leagueId, platformId, partner, signal, validToken,forceRefresh)
                  .catch((error) => {
                    // setStartEndpointError(error);
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, Start: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        const errorKey = `start-${platformId}-${leagueId}`;
                        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
                      }
                    }
                  })
                  .finally(() => {
                    activeRequests.current.start.isLoading = false;
                  });
              }
              return Promise.resolve();
            })() */
          ]);

          // Wait for super analysis to complete before enabling dependent buttons
          try {
            await superAnalysisPromise;
            setIsSuperAnalysisComplete(true);

            // AUTO-PROCESS AI: when ON, trigger all 4 stream endpoints in parallel (10 credits each = 40 total)
            const settingsToUse = calculatorSettingsOverride !== undefined ? calculatorSettingsOverride : calculatorSettings;
            const modelToUse: "RotoGPT 5.0" | "RotoGPT FREE" = (userSubscription || "free").toLowerCase() === "free" ? "RotoGPT FREE" : "RotoGPT 5.0";
            const subscriptionIsFree = modelToUse === "RotoGPT FREE";
            const currentFreeCredits = freeCredits ?? 0; // this is the credits from the free model
            const currentCredits = credits ?? 0; // this is the credits from the subscription + booster credits
            const autoProcessMinCredits = frontEnvNumber("REACT_APP_AUTO_PROCESS_MIN_CREDITS", 40); // 10 per endpoint × 4

            if (aiSettings?.autoProcessAI && !isUnsyncedPlatform(platformId)) {
              // Same credit guard as SectionHomePage (waiver/start/rundown/draft buttons): block execution and open drawer or paywall
              if (subscriptionIsFree && currentFreeCredits <= 9) {
                //   console.log("[Auto-Process] Opening BenchDrawer: No freeCredits available");
                // Require at least 40 credits to run all 4; otherwise open drawer or paywall and skip
                // if (subscriptionIsFree && currentFreeCredits < AUTO_PROCESS_MIN_CREDITS) {
                if (!isLoggedInAuth0ExperienceGlobal) {
                  void redirectToAuth0NoCredits(loginWithRedirect);
                } else {
                  setInitialPaymentView?.("manageMembership");
                  setIsPaymentOpen?.(true);
                }
              } else if (enableCreditValidation && !subscriptionIsFree && currentCredits <= 9) {
                // } else if (enableCreditValidation && !subscriptionIsFree && currentCredits < AUTO_PROCESS_MIN_CREDITS) {
                setInitialPaymentView?.("manageMembership");
                setIsPaymentOpen?.(true);
              } else {
                setLoadingWaiver(true);
                setLoadingStart(true);
                setLoadingRundown(true);
                // setLoadingDraftAdvice(true);
                setLoadingDraftAdvice(!!canRunDraftAdvice);
                setEndpointErrors((prev) => ({
                  ...prev,
                  [`waiver-${platformId}-${leagueId}`]: false,
                  [`start-${platformId}-${leagueId}`]: false,
                  [`rundown-${platformId}-${leagueId}`]: false,
                  [`DraftAdvice-${platformId}-${leagueId}`]: false,
                }));
                // Fire all 4 in parallel; use same signal so refresh/team change can cancel. Do not await.

                if (showWaiver) {
                  fetchWaiverDataStream(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse, modelToUse).catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, Waiver: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        setEndpointErrors((prev) => ({ ...prev, [`waiver-${platformId}-${leagueId}`]: true }));
                      }
                    }
                  });
                }

                if (showStart) {
                  fetchStartDataStream(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse, modelToUse).catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, Start: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        setEndpointErrors((prev) => ({ ...prev, [`start-${platformId}-${leagueId}`]: true }));
                      }
                    }
                  });
                }

                if (showTeamAnalysis) {
                  fetchRundownDataStream(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse, modelToUse).catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, Rundown: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        setEndpointErrors((prev) => ({ ...prev, [`rundown-${platformId}-${leagueId}`]: true }));
                      }
                    }
                  });
                }

                if (canRunDraftAdvice) {
                  fetchDraftAdviceDataStream(leagueId, platformId, partner, signal, validToken, forceRefresh, settingsToUse, modelToUse).catch((error) => {
                    if (error.name === "AbortError") throw error;
                    if (!returnedFromBackground) {
                      const errorMessage = error?.message || "";
                      setErrorMessages((prev) => ({ ...prev, DraftAdvice: errorMessage }));
                      if (errorMessage.trim() !== noDraftErrorMessage) {
                        setEndpointErrors((prev) => ({ ...prev, [`DraftAdvice-${platformId}-${leagueId}`]: true }));
                      }
                    }
                  });
                }
              }
            } else {
              // AUTO-PROCESS AI IS OFF: optionally check Redis and auto-load cached Waivers/Start/Rundown/DraftAdvice on team select.
              // When forceRefresh is true (Refresh League Data / handleResetLeagueSettings), skip cache-only — sectionData was cleared
              // and the user expects on-demand runs without repopulating from Redis here.
              // Un-Synced: those agents are blocked — skip cache-check requests (same guard as autoProcessAI ON branch).
              if (!forceRefresh && !isUnsyncedPlatform(platformId)) {
                const catchCacheError = (error: any, section: string) => {
                  if (error.name !== "AbortError") {
                    console.error(`Cache check error for ${section}:`, error);
                  }
                };

                if (showWaiver) {
                  fetchWaiverDataStream(leagueId, platformId, partner, signal, validToken, false, settingsToUse, modelToUse, true)
                    .then((res) => {
                      if (res === "NOT_CACHED") setLoadingWaiver(false);
                    })
                    .catch((e) => catchCacheError(e, "Waiver"));
                }
                if (showStart) {
                  fetchStartDataStream(leagueId, platformId, partner, signal, validToken, false, settingsToUse, modelToUse, true)
                    .then((res) => {
                      if (res === "NOT_CACHED") setLoadingStart(false);
                    })
                    .catch((e) => catchCacheError(e, "Start"));
                }
                if (showTeamAnalysis) {
                  fetchRundownDataStream(leagueId, platformId, partner, signal, validToken, false, settingsToUse, modelToUse, true)
                    .then((res) => {
                      if (res === "NOT_CACHED") setLoadingRundown(false);
                    })
                    .catch((e) => catchCacheError(e, "Rundown"));
                }
                if (canRunDraftAdvice) {
                  fetchDraftAdviceDataStream(leagueId, platformId, partner, signal, validToken, false, settingsToUse, modelToUse, true)
                    .then((res) => {
                      if (res === "NOT_CACHED") setLoadingDraftAdvice(false);
                    })
                    .catch((e) => catchCacheError(e, "DraftAdvice"));
                }
              }
            }
          } catch (error) {
            // If super analysis was rate-limited, open drawer and do not rethrow to avoid global uncaught error
            if ((error as any).isRateLimit) {
              openRateLimitDrawer?.((error as any).retryAfter);
              setIsSuperAnalysisComplete(true);
            } else {
              if (error.name !== "AbortError") {
                console.error("Super analysis promise failed:", error);
                setIsSuperAnalysisComplete(true);
              }
            }
            // Even if it fails (if not aborted), we consider the analysis "completed" to unlock the UI buttons.
          }

          //--------------------------------
          // Only if all the endpoints are completed successfully
          if (
            !isTransitioning &&
            sectionData.commonLeagueData !== null &&
            // sectionData.waiverSuggestions !== null &&
            sectionData.news !== null &&
            sectionData.rosters !== null &&
            sectionData.rankings !== null &&
            sectionData.allRankings !== null &&
            sectionData.waiver !== null &&
            sectionData.rundown !== null &&
            sectionData.DraftAdvice !== null &&
            sectionData.start !== null
          ) {
            setDataLoadedFromTeamsPage(true);
          }

          // If everything is completed successfully
          // dont reset the flag here
          // setReturnedFromBackground(false);
        } catch (error) {
          if (error.name === "AbortError") {
            console.log("Requests cancelled due to platform switch");
            setErrorMessages((prev) => ({ ...prev, "League Data": error.message }));
            setIsChangingPlatform(false);
            return; // This will stop the execution of the function (no error message in ui)
          }

          console.log("🔴 TeamsDropdown - error:", error);
          console.log("🔴 TeamsDropdown - error.message:", error.message);

          // 🆕 NEW: Detect ESPN authentication errors
          // if (currentUserDataPlatform?.integration_name === "espn") {
          //   console.log("🔴 ESPN ERROR DETECTED - Full details:");
          //   console.log("🔴 Error message:", error.message);
          //   console.log("🔴 Error name:", error.name);
          //   console.log("🔴 Error object:", error);

          //   //console.log("🔴 ESPN session expired, opening login form");
          //   // setLoggingInTo("ESPN");
          //   // setLoginError("Please connect again");
          //   // return; // Stop execution
          // }

          // If common_league_data fails
          setCommonLeagueDataFailed(true);

          // Disable all loading states
          setLoadingNews(false);
          setLoadingWaiver(false);
          setLoadingStart(false);
          setLoadingRoster(false);
          setLoadingRankings(false);
          setLoadingAllRankings(false);
          setLoadingRundown(false);
          setLoadingDraftAdvice(false);

          // const customMessage = `${error.message}. Try with a different league`;
          setErrorMessages((prev) => ({ ...prev, "League Data": error.message }));
          // setErrorMessages((prev) => ({ ...prev, "League Data": "Something went wrong" }));

          // If there is an error and we did not come back from background, show retry
          if (!returnedFromBackground) {
            setEndpointErrors((prev) => ({ ...prev, "League Data": error.message }));
          }

          // Even if the common_league_data fails, we mark super_analysis as complete so that
          // the UI buttons can show their error states instead of staying in "Analyzing..."
          setIsSuperAnalysisComplete(true);
        } finally {
          // Clear thoughts when the whole process is finished
          setThoughtsCommon("");
          // Clear abort controller if we created it
          if (abortControllerRef.current) {
            abortControllerRef.current = null;
          }
          isExecutingRef.current = false;
          // Step 3: Reset the flag in the finally block to be ready for the next event.
          if (returnedFromBackground) {
            setReturnedFromBackground(false);
          }

          // 🆕 NEW: Deactivate refreshing state and set super analysis complete when the process is finished
          if (shouldDeactivateRefreshing) {
            setRefreshingLeagueData(false);
            setIsSuperAnalysisComplete(true);
          }
          // setTimeout(() => {
          //   setIsTransitioning(false);
          // }, 100);
          // NOTE: The task is finished in the *outer* finally block
          // to ensure it runs even if errors occur before this point.
        }
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Requests cancelled due to platform switch");
          setErrorMessages((prev) => ({ ...prev, "League Data": error.message }));
          setIsChangingPlatform(false);
          return; // This will stop the execution of the function (no error message in ui)
        }

        // If common_league_data fails
        setCommonLeagueDataFailed(true);

        // Disable all loading states
        setLoadingNews(false);
        setLoadingWaiver(false);
        setLoadingStart(false);
        setLoadingRoster(false);
        setLoadingRankings(false);
        setLoadingAllRankings(false);
        setLoadingRundown(false);
        setLoadingDraftAdvice(false);

        // const customMessage = `${error.message}. Try with a different league`;
        setErrorMessages((prev) => ({ ...prev, "League Data": error.message }));
        // setErrorMessages((prev) => ({ ...prev, "League Data": "Something went wrong" }));

        // If there is an error and we did not come back from background, show retry
        if (!returnedFromBackground) {
          setEndpointErrors((prev) => ({ ...prev, "League Data": error.message }));
        }

        // Even if the common_league_data fails, we mark super_analysis as complete so that
        // the UI buttons can show their error states instead of staying in "Analyzing..."
        setIsSuperAnalysisComplete(true);
      } finally {
        // Clear abort controller if we created it
        if (abortControllerRef.current) {
          abortControllerRef.current = null;
        }
        isExecutingRef.current = false;
        setTimeout(() => {
          setIsTransitioning(false);
        }, 100);
        // 🆕 NEW: Deactivate refreshing state and set super analysis complete when the process is finished
        if (shouldDeactivateRefreshing) {
          setRefreshingLeagueData(false);
          setIsSuperAnalysisComplete(true);
        }
        // Finish the background task if it's running
        if (Capacitor.isNativePlatform() && taskId) {
          console.log(`handlePartnerClick: Finishing background task: ${taskId}`);
          BackgroundTask.finish({ taskId });
        }
      }
    };

    // Cleanup effect to abort pending requests when component unmounts
    useEffect(() => {
      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, []);

    useEffect(() => {
      // This entire recovery mechanism is only for mobile platforms.
      if (!Capacitor.isNativePlatform() && !isMobileWebBrowser()) {
        return;
      }
      const reTriggerIfLocked = async () => {
        const commonCtx = lastCommonContextRef.current;
        if (commonCtx) {
          const key = `${authId}-common-${commonCtx.platformId}-${commonCtx.leagueId}`;
          if (activeCommonSuperRequests.current.has(key)) {
            // Use calculatorSettings prop for recovery flow (calculatorSettingsOverride not available here)
            await startPollingForCompletion({
              endpointType: "common",
              sectionKey: "commonLeagueData",
              leagueId: commonCtx.leagueId,
              teamName: commonCtx.teamName,
              yahooToken: undefined,
              signal: undefined,
              context: { platformId: commonCtx.platformId, leagueId: commonCtx.leagueId, userId: authId },
              getAccessTokenSilently: getSafeAccessAuth0Token,
              authId,
              currentUserDataPlatform: userDataCache[commonCtx.platformId],
              pocToken,
              suppressLoading: true,
              apiCall: (_auth, udp, lg, _tm, poc, token, sig, ytok, apiCallArgs) => {
                const calcSettings = apiCallArgs?.calculatorSettings;
                return getCommonLeagueDataStream(udp, lg, poc, token, sig, ytok, undefined, calcSettings, superAnalysisModel, commonCtx.platformId);
              },
              apiCallArgs: { calculatorSettings: calculatorSettings },
              setStreamState: () => { },
              setData: () => { },
              setLoading: () => { },
              updateSectionData: (_section, data) => {
                const withMeta = { ...data, timestamp: new Date().toISOString(), platform: commonCtx.platformId, teamName: commonCtx.teamName, leagueId: commonCtx.leagueId };
                updateSectionData("commonLeagueData", withMeta);
              },
              setEndpointErrors: () => { },
              setErrorMessages,
              setHasAnimationCompleted: () => { },
              requestKey: key,
              activeStreamRequests: activeCommonSuperRequests,
              retrievingMessage: "Retrieving your league data...",
              errorKey: "League Data",
              currentTeamInfoRef: { current: { platformId: commonCtx.platformId, leagueId: commonCtx.leagueId } } as any,
              setThoughtsCommon,
              onComplete: (finalData) => {
                try {
                  if (finalData?.league_info?.league_type) setCommonLeagueLeagueType(finalData.league_info.league_type);
                } catch { }
              },
            }).finally(() => {
              activeCommonSuperRequests.current.delete(key);
              console.log(`[Background Recovery] Common polling finished for ${commonCtx.leagueId}`);
            });
          }
        }

        const superCtx = lastSuperContextRef.current;
        if (superCtx) {
          const key = `${authId}-super-${superCtx.platformId}-${superCtx.leagueId}`;
          if (activeCommonSuperRequests.current.has(key)) {
            // Use calculatorSettings prop for recovery flow (calculatorSettingsOverride not available here)
            await startPollingForCompletion({
              endpointType: "super",
              sectionKey: "super",
              leagueId: superCtx.leagueId,
              teamName: superCtx.teamName,
              yahooToken: undefined,
              signal: undefined,
              context: { platformId: superCtx.platformId, leagueId: superCtx.leagueId, userId: authId },
              getAccessTokenSilently: getSafeAccessAuth0Token,
              authId,
              currentUserDataPlatform: userDataCache[superCtx.platformId],
              pocToken,
              suppressLoading: true,
              apiCall: (_auth, udp, lg, _tm, poc, token, sig, ytok, apiCallArgs) => {
                const calcSettings = apiCallArgs?.calculatorSettings;
                return getSuperAnalysisStream(_auth, udp, lg, _tm, poc, token, sig, ytok, undefined, calcSettings, superAnalysisModel, superCtx.platformId);
              },
              apiCallArgs: { calculatorSettings: calculatorSettings },
              setStreamState: () => { },
              setData: () => { },
              setLoading: () => { },
              updateSectionData: () => { },
              setEndpointErrors: () => { },
              setErrorMessages,
              setHasAnimationCompleted: () => { },
              requestKey: key,
              activeStreamRequests: activeCommonSuperRequests,
              retrievingMessage: "Retrieving your super analysis...",
              errorKey: "League Data",
              currentTeamInfoRef: { current: { platformId: superCtx.platformId, leagueId: superCtx.leagueId } } as any,
              setThoughtsCommon,
            }).finally(() => {
              activeCommonSuperRequests.current.delete(key);
              console.log(`[Background Recovery] Super polling finished for ${superCtx.leagueId}`);
            });
          }
        }
      };

      let nativeListener: any;
      const onVis = () => {
        if (document.visibilityState === "visible") void reTriggerIfLocked();
      };

      if (Capacitor.isNativePlatform()) {
        App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) void reTriggerIfLocked();
        }).then((l) => (nativeListener = l));
      } else {
        document.addEventListener("visibilitychange", onVis);
      }

      return () => {
        if (nativeListener?.remove) nativeListener.remove();
        document.removeEventListener("visibilitychange", onVis);
      };
    }, [authId]);

    // Reset commonLeagueDataFailed when platform changes
    useEffect(() => {
      setCommonLeagueDataFailed(false);
    }, [selectedTeams, userDataPlatform]);

    //useffect to check if the page is refreshed and if the data is loaded from the teams page
    useEffect(() => {
      const handlePageRefresh = async () => {
        const isPageRefresh = performance.navigation.type === 1 || !partnerClicked;
        const currentPlatform = leaguePlatforms.find((p) => p.isSelected)?.id;

        // Execute the function only if the page is refreshed and the data is loaded from the teams page
        if (isPageRefresh && currentPlatform && selectedTeamInfo && teamsData?.leagues && pocToken) {
          const teamExists = validateTeamData(selectedTeamInfo, teamsData);

          if (teamExists) {
            if (
              !isTransitioning &&
              sectionData &&
              sectionData.commonLeagueData !== null &&
              sectionData.news !== null &&
              sectionData.rosters !== null &&
              sectionData.rankings !== null &&
              sectionData.allRankings !== null &&
              sectionData.waiver !== null &&
              sectionData.rundown !== null &&
              sectionData.DraftAdvice !== null &&
              sectionData.start !== null
            ) {
              return;
            } else {
              setLoadingNews(showNews);
              setLoadingWaiver(true);
              setLoadingStart(true);
              setLoadingRoster(showRosters);
              setLoadingRankings(showAllRankings);
              setLoadingAllRankings(showAllRankings);
              setLoadingRundown(true);
              setLoadingDraftAdvice(true);
              setHasLoadingStarted(true);

              await new Promise((resolve) => setTimeout(resolve, 50));
              setMessage("Restoring your team information...");
              // await handlePartnerClick(selectedTeamInfo.teamName, selectedTeamInfo.leagueId, currentSelectedPlatformIdRef.current);
            }
          }
        }
      };

      handlePageRefresh();
    }, [selectedTeamInfo, teamsData, partnerClicked, pocToken, leaguePlatforms, sectionData]);

    // Shared funny team name audio (manual click + platform auto-select)
    const playFunnyTeamAudio = (teamName: string, leagueId: string, platformId: string) => {
      // Bless summaryPlayer during this user gesture (mobile web) for delayed playback after super analysis.
      audioService.unlockAudio();
      // --- NEW AUDIO LOGIC ---
      // 1. Abort any previous, still-running audio request from a different click.
      if (audioAbortControllerRef.current) {
        audioAbortControllerRef.current.abort();
      }
      // 2. Create a new controller for this specific request.
      const controller = new AbortController();
      audioAbortControllerRef.current = controller;

      // const platformUserId = userDataPlatform?.user_id;
      const currentUserDataForAudio = userDataCache[platformId];
      // FIX: Fallback logic for platforms where user_id might be missing
      // For MFL, YAHOO, and ESPN: backend uses leagueToken (token field) as platformUserId
      // For other platforms: use user_id, swid, or username as fallback
      const integrationName = currentUserDataForAudio?.integration_name?.toLowerCase();
      let platformUserId;
      if (integrationName === "mfl" || integrationName === "yahoo") {
        // These platforms use token (leagueToken) as platformUserId
        // IMPORTANT: Backend truncates token to 50 chars in GCP storage, so we must match that
        const token = currentUserDataForAudio?.token;
        platformUserId = token && token.length > 50 ? token.substring(0, 50) : token;
      } else {
        // For other platforms, use user_id, swid, or username as fallback
        platformUserId = currentUserDataForAudio?.user_id || currentUserDataForAudio?.swid || currentUserDataForAudio?.username;
      }
      // console.log("TEAMS DROPDOWN - platformUserId", platformUserId);
      // 3. Fire-and-forget the audio generation process. This runs in parallel
      //    and does not block the main UI update.
      // Run All Agents: skip team name audio so lipsync does not interrupt the sign/Loading flow after super analysis.
      if (platformUserId && pocToken && !aiSettings?.autoProcessAI) {
        (async () => {
          let audio: HTMLAudioElement | undefined;
          try {
            const accessAuthToken = await getSafeAccessAuth0Token();
            if (!accessAuthToken) return;

            const audioFetchStartTime = Date.now();
            // generateFunnyAudio now returns { audioUrl, lipSyncData }
            const { audioUrl, lipSyncData } = await generateFunnyAudio(pocToken, accessAuthToken, teamName, leagueId, platformUserId, controller.signal);

            const audioFetchDuration = Date.now() - audioFetchStartTime;
            const seconds = audioFetchDuration / 1000;
            console.log(`[Funny Audio] Frontend received audio URL and lip sync data from backend in ${seconds.toFixed(2)}s.`);

            if (!audioUrl) {
              console.log("[Funny Audio] No audio URL received from backend.");
              return;
            }

            prefetchVegaFromAudioUrl(audioUrl);

            // Capacitor: do not construct HTMLAudio yet — a second media element glitches the welcome tap.
            if (!isCapacitorNative()) {
              audio = new Audio(audioUrl);
              audio.setAttribute("playsinline", "true");
            }

            // Delay only playback/lipsync until super analysis is complete.
            await waitForSuperAnalysisToComplete();
            if (controller.signal.aborted) return;

            // Vega: finish visemes while welcome still plays. Do not stop welcome until team-name is ready to play().
            let preparedVega = false;
            if (isVegaLipSyncEnabled()) {
              preparedVega = await prepareVegaFromAudioUrl(audioUrl);
              if (controller.signal.aborted) return;
            }

            if (!audio) {
              audio = new Audio(audioUrl);
              audio.setAttribute("playsinline", "true");
            }

            let usedVega = false;
            if (characterRef?.current) {
              if (preparedVega) {
                audioService.stopCurrentAudio();
                characterRef.current.setIsTalking(false);
                usedVega = tryApplyVegaFromAudioUrlSync(audioUrl, characterRef.current, "funny");
              }
              if (!usedVega && lipSyncData) {
                console.log(`[Funny Audio] Setting up lip sync with ${lipSyncData.mouthCues?.length || 0} cues`);
                applyLegacyLipSyncOrSkip(characterRef.current, lipSyncData, "funny");
              }
            }

            // Capacitor Vega: bind before play so the heard clock starts with the first sample (welcome-style).
            if (isCapacitorNative() && usedVega && characterRef?.current) {
              characterRef.current.setAudioElement(audio);
            }

            const playedSuccessfully = audioService.playAudio(audio, AudioPriority.MEDIUM);

            // Use the element that actually plays (summaryPlayer on mobile web, same as endpoint summaries).
            const actualPlayingAudio = audioService.getNowPlayingAudio() ?? audio;
            if ((usedVega || lipSyncData) && characterRef?.current) {
              // Note: setAudioElement now handles the 'ended' event internally
              // and automatically switches back to Idle animation
              characterRef.current.setAudioElement(actualPlayingAudio);
              characterRef.current.setIsTalking(true);
            }

            if (!playedSuccessfully) {
              // If the service decided not to play, there's nothing to clean up
              // as we are not using Blob URLs anymore.
              console.log("[Funny Audio] Audio playback was skipped by the audio service.");
              // Also stop talking animation if audio was skipped
              if (characterRef?.current) {
                characterRef.current.setIsTalking(false);
              }
            }
          } catch (audioError) {
            if (audioError.name === "AbortError") {
              console.log("Funny audio request was cancelled.");
              return;
            }
            console.error("Failed to generate or play funny audio:", audioError);
            // Stop talking animation on error
            if (characterRef?.current) {
              characterRef.current.setIsTalking(false);
            }
          }
        })();
      } else {
        // DEBUG: Log para ver por qué se saltó la generación si sigue fallando
        console.warn("[Funny Audio] Skipped generation. Missing ID or Token:", {
          platformUserId,
          hasPocToken: !!pocToken,
          platformId,
          userDataKeys: currentUserDataForAudio ? Object.keys(currentUserDataForAudio) : "no-data",
          currentUserDataForAudio: currentUserDataForAudio,
        });
      }
      // --- END OF NEW AUDIO LOGIC ---
    };

    // UseImperativeHandle to handle the handlePartnerClick function from the parent component
    useImperativeHandle(ref, () => ({
      handlePartnerClick,
      playFunnyTeamAudio,
    }));

    // Add at component level with other functions
    const handleTeamSelectionClick = async (teamName: string, leagueId: string, currentSelectedPlatformIdRef) => {
      const platformId = currentSelectedPlatformIdRef?.current;
      // Already on this league and on-screen data is still inside the cache window: close the list only.
      // Do not cancel in-flight work and do not call handlePartnerClick.
      const alreadyOnThisLeague =
        selectedTeamInfo?.platformId === platformId &&
        selectedTeamInfo?.leagueId === leagueId &&
        selectedTeamInfo?.teamName === teamName;
      // Same league still loading, or cache still fresh: close the list only.
      // Do not abort the in-flight run. Expired cache falls through and refetches.
      const common = sectionData?.commonLeagueData;
      const hasThisLeagueTimestamp = Boolean(
        common?.platform === platformId &&
        common?.leagueId === leagueId &&
        common?.teamName === teamName &&
        common?.timestamp
      );
      if (
        alreadyOnThisLeague &&
        (!hasThisLeagueTimestamp || isCacheValid(sectionData, currentSelectedPlatformIdRef, teamName, leagueId))
      ) {
        if (expanded) setExpanded("");
        return;
      }
      const currentUserDataForAudio = userDataCache[platformId];
      playFunnyTeamAudio(teamName, leagueId, platformId);

      try {
        // console.log("DEBUG teamsData:", teamsData);
        // console.log("DEBUG userDataPlatform:", userDataPlatform);
        // console.log("DEBUG leaguePlatforms:", leaguePlatforms);
        setNewClickOnPlatofrm(true);

        // Manual pick: collapse teams accordion (auto-restore must leave it open)
        if (expanded) {
          setExpanded("");
        }

        // This event tracks every single click. We keep it as per previous implementation.
        await trackLeagueSelection(authId, platformId, leagueId, teamName);

        // --- Logic for the Summary Event ---
        const totalTeams = teamsData?.leagues?.length || 0;
        const info = managedTeamsInfoRef.current;

        // Key Point: Check if the context (platform OR league) has changed.
        if (info.platformId !== platformId || info.userId !== currentUserDataForAudio?.user_id) {
          // Context changed - send summary for previous context
          sendManagedTeamsSummaryIfNeeded();

          // Reset for new context
          managedTeamsInfoRef.current = {
            authId: authId,
            platformId,
            userId: currentUserDataForAudio?.user_id, // The new league context
            totalTeams,
            managedSet: new Set([leagueId]), // Start counting with the current team
          };
        } else {
          // If the context is the same, simply add the new team to the set.
          info.managedSet.add(leagueId);
          info.totalTeams = totalTeams; // Update the total in case it changed
        }

        // CRITICAL FIX: Check if calculator_settings exist for this team in MongoDB (via calculatorSettingsMap)
        // If they exist, pass them to the endpoints so they are used even after cache expires
        const teamKey = `${platformId}-${leagueId}`;
        const existingCalculatorSettings = calculatorSettingsMap[teamKey] || null;
        console.log(`🔍 [TeamsDropdown] Team selection - checking for existing calculatorSettings for ${teamKey}:`, existingCalculatorSettings ? "FOUND" : "NOT FOUND");

        const teamSelectionPromise = handlePartnerClick(teamName, leagueId, currentSelectedPlatformIdRef.current, undefined, false, existingCalculatorSettings);
        await teamSelectionPromise;
        setNewClickOnPlatofrm(false);
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Team selection aborted");
          return;
        }
        throw error;
      }
    };

    // This useEffect is now only for cleanup on unmount
    useEffect(() => {
      return () => {
        // Cleanup on unmount: abort any pending audio request
        if (audioAbortControllerRef.current) {
          audioAbortControllerRef.current.abort();
        }
      };
    }, []); // Empty dependency array ensures this runs only on mount and unmount

    return (
      <>
        {/* IDP Dialog */}
        <IdpDialog open={showIdpDialog} onClose={() => setShowIdpDialog(false)} />
        {/* NEW: platform-style cards (left strip orange = selected, gray = not) */}
        <Stack
          direction="column"
          spacing={1}
          sx={{
            width: "100%",
            /* Single scroll container: AccordionDetails (.accordionDetailTeams) handles scroll */
            // p: 1,
            py: 0,
            pt: 0.5,
            // Extra pb so last league clears the fixed FooterSignUp (taller desktop padding)
            pb: isMobile ? 7 : 6,
            mb: 3,
            boxSizing: "border-box",
          }}
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="stretch"
          bgcolor="#000000"
        >
          {/* {teamsData?.leagues?.map((team) => { */}
          {/* Display-only: pin selected league to top. Does not mutate teamsData / cache / prefs. */}
          {[...(teamsData?.leagues ?? [])]
            .sort((a, b) => {
              const selectedId = selectedTeamInfo?.leagueId;
              if (!selectedId) return 0;
              if (a.league_id === selectedId) return -1;
              if (b.league_id === selectedId) return 1;
              return 0;
            })
            .map((team) => {
              const isSelected = selectedTeamInfo?.leagueId === team.league_id;
              // League type subtitle: only on selected + synced league, from commonLeagueData (same source as SectionHomePage)
              const commonData = sectionData?.commonLeagueData;
              const commonMatchesSelectedTeam =
                Boolean(isSelected) &&
                Boolean(selectedTeamInfo) &&
                !isUnsyncedPlatform(selectedTeamInfo?.platformId) &&
                commonData?.leagueId === team.league_id &&
                commonData?.platform === selectedTeamInfo?.platformId;

              // Get the league type from the commonLeagueData
              const leagueTypeRaw = commonMatchesSelectedTeam
                ? commonData?.league_info?.league_type
                : null;

              // Normalize the league type to uppercase
              const leagueTypeLabel =
                typeof leagueTypeRaw === "string" && leagueTypeRaw.trim()
                  ? leagueTypeRaw.trim().toUpperCase()
                  : null;

              return (
                <Button
                  key={team.league_id}
                  fullWidth
                  variant="contained"
                  disableElevation
                  data-team-id={team.name}
                  onClick={() => handleTeamSelectionClick(team.name, team.league_id, currentSelectedPlatformIdRef)}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    height: "70px",
                    borderRadius: "8px",
                    p: 0,
                    textTransform: "none",
                    // Left status strip: orange = selected team, gray = not selected
                    border: "none !important",
                    borderLeft: `10px solid ${isSelected ? "#FF672D" : "#555555"} !important`,
                    backgroundColor: "#222222",
                    boxShadow: "none !important",
                    "&:hover": {
                      border: "none !important",
                      borderLeft: `10px solid ${isSelected ? "#FF672D" : "#555555"} !important`,
                      backgroundColor: "#222222",
                      boxShadow: "none !important",
                    },
                    "&:active, &.Mui-focusVisible": {
                      border: "none !important",
                      borderLeft: `10px solid ${isSelected ? "#FF672D" : "#555555"} !important`,
                      boxShadow: "none !important",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      width: "100%",
                      minWidth: 0, // needed so text-overflow ellipsis works inside flex
                      px: 1.5,
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "24px",
                        textAlign: "left",
                        lineHeight: 1.2,
                        fontFamily: "Roboto Condensed",
                        // Mobile: always one line; browser clips by width (not fixed char count)
                        ...(isMobile
                          ? {
                            width: "100%",
                            maxWidth: "100%",
                            whiteSpace: "nowrap", // don't wrap the text (not jumping lines to next line)
                            overflow: "hidden", // hide the overflowed text (cut it off)
                            textOverflow: "ellipsis", // add an ellipsis (...) to the overflowed text
                          }
                          : {}),
                      }}
                    >
                      {team.name}
                    </Typography>
                    {/* Synced selected league only — dynasty / redraft / keeper from commonLeagueData */}
                    {leagueTypeLabel && (
                      <Typography
                        sx={{
                          color: "#FF672D",
                          fontWeight: 600,
                          fontSize: "16px",
                          textAlign: "left",
                          lineHeight: 1.2,
                          // letterSpacing: "0.02em",
                          fontFamily: "Roboto Condensed",
                        }}
                      >
                        {leagueTypeLabel}
                      </Typography>
                    )}
                  </Box>
                </Button>
              );
            })}
        </Stack>

        {/* OLD: vertical list with white separators (kept for easy rollback)
        <Stack
          direction="column"
          spacing={0}
          sx={{
            width: "100%",
          }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="center"
          bgcolor="#000000"
        >
          {teamsData?.leagues?.map((team) => (
            <Button
              key={team.league_id}
              variant="contained"
              data-team-id={team.name}
              sx={{
                width: "90%",
                "&:first-of-type": {
                  borderTop: "1px solid #ffffff !important",
                },
                "&:last-child": {
                  borderBottomLeftRadius: "10px !important",
                  borderBottomRightRadius: "10px !important",
                  borderBottom: "0px solid #ffffff !important",
                },
              }}
              color="info"
              className={styles.buttonTeamName}
              onClick={() => handleTeamSelectionClick(team.name, team.league_id, currentSelectedPlatformIdRef)}
            >
              {team.name}
            </Button>
          ))}
        </Stack>
        */}
      </>
    );
  }
);

export default TeamsDropdown;
